/// <amd-module name="Wit/Common/WorkItemDialogs/Components/DeleteDialog" />
import * as React from "react";
import { useRef, useState } from "react";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { usePageContext } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { Icon, IconSize } from "VSSUI/Icon";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";

export interface IDeleteDialogProps<TWorkItem extends INamedFieldDataDictionary> {
    onDeleteItems?: (workItems: TWorkItem[]) => void;
    onDeletionFailed?: (workItems: TWorkItem[]) => void;
    onDeleteFinished?: () => void;
    onDismiss?: () => void;
    selectedItems: TWorkItem[];
}

export function DeleteDialog<TWorkItem extends INamedFieldDataDictionary>({
    onDeleteItems,
    onDeleteFinished,
    onDeletionFailed,
    onDismiss,
    selectedItems
}: IDeleteDialogProps<TWorkItem>) {
    const pageContext = usePageContext();
    const [deleting, setDeleting] = useState(false);
    const unhandledFailedIds = useRef<number[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const deleteWorkItem = async () => {
        setDeleting(true);

        const batches = batch(selectedItems, 200);
        const deletionPromises = batches.map(async workItemBatch => {
            const { successes, failures } = await deleteWorkItems(pageContext, workItemBatch);
            onDeleteItems?.(successes);

            if (failures.length > 0) {
                if (onDeletionFailed) {
                    onDeletionFailed(failures);
                } else {
                    unhandledFailedIds.current.push(...(failures.map(f => f[CoreFieldRefNames.Id]).filter(id => !!id) as number[]));
                }
            }
        });

        await Promise.all(deletionPromises);

        onDeleteFinished?.();
        setDeleting(false);

        if (unhandledFailedIds.current.length > 0) {
            setErrorMessage(format(Resources.DeletingFailedMessage, unhandledFailedIds.current.join(", ")));
        } else {
            onDismiss?.();
        }
    };

    return (
        <Dialog
            titleProps={{ text: Resources.DeleteWorkItems }}
            onDismiss={() => onDismiss?.()}
            footerButtonProps={[
                {
                    text: Resources.Cancel,
                    onClick: () => onDismiss?.()
                },
                {
                    text: Resources.DeleteSelectedWorkItems,
                    onClick: () => deleteWorkItem(),
                    primary: true,
                    disabled: deleting || !!errorMessage
                }
            ]}
            escDismiss={false}
            lightDismiss={false}
            overlay={deleting ? { spinnerLabel: Resources.DeletingWorkItems } : undefined}
        >
            {errorMessage ? <MessageCard severity={MessageCardSeverity.Warning}>{errorMessage}</MessageCard> : null}
            <div className="scroll-auto flex-grow flex-row">
                <Icon className="delete-dialog-warning-icon" iconName="IncidentTriangle" size={IconSize.large} />
                <div>
                    <p>{Resources.DeleteWorkItemsConfirmation}</p>
                    <p>{Resources.DeleteWorkItemsRestore}</p>
                </div>
            </div>
        </Dialog>
    );
}

interface IDeleteWorkItemsRequest {
    ids: number[];
}

interface IDeleteWorkItemsResponse {
    successes: number[];
    failures: number[];
}

interface IDeleteWorkItemsResult<TWorkItem> {
    successes: TWorkItem[];
    failures: TWorkItem[];
}

async function deleteWorkItems<TWorkItem extends INamedFieldDataDictionary>(
    pageContext: IVssPageContext,
    workItems: TWorkItem[]
): Promise<IDeleteWorkItemsResult<TWorkItem>> {
    const ids = workItems.map(i => i[CoreFieldRefNames.Id]).filter(id => !!id) as number[];
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");

    const deletionResponse = await contributionService.getDataAsync<IDeleteWorkItemsResponse, IDeleteWorkItemsRequest>(
        "ms.vss-work-web.delete-work-items-data-provider",
        { ids },
        true
    );

    if (deletionResponse) {
        const successes = deletionResponse.successes
            .map(id => workItems.find(item => item[CoreFieldRefNames.Id] === id))
            .filter(item => !!item) as TWorkItem[];

        const failures = deletionResponse.failures
            .map(id => workItems.find(item => item[CoreFieldRefNames.Id] === id))
            .filter(item => !!item) as TWorkItem[];

        return { successes, failures };
    } else {
        return { successes: [], failures: workItems };
    }
}

/**
 * Given an array of items and a batch size, return an array of smaller arrays whose size is at most the batch size
 */
function batch<T>(items: T[], batchSize: number): T[][] {
    const batches: T[][] = [];

    for (let i = 0; i < items.length; i += batchSize) {
        batches.push(items.slice(i, i + batchSize));
    }

    return batches;
}
